<template>
    <v-navigation-drawer
      left
      permanent
      clipped
      height="100%"
      :width="filtersWidth"
    >
      <v-list
        class="z-99 px-4"
        nav
      >
        <DateRangeFilter
          :from="dateFrom"
          :to="dateTo"
          @fromInput="handleDateFromInput"
          @toInput="handleDateToInput"
        />
        <v-divider />
        <AreaFilter
          :selectedAreas="selectedAreas"
          :areas="areas"
          @selectedAreasInput="handleSelectedAreaInput"
        />
        <v-divider />
        <ContractsFilter
          :selectedContracts="selectedContracts"
          :contracts="contracts"
          @selectedContractsInput="handleSelectedContractsInput"
        />
        <v-divider />
        <PackagesFilter
          :selectedPackages="selectedPackages"
          :packages="packages"
          @selectedPackagesInput="handleSelectedPackagesInput"
        />
        <v-divider />
        <ContractorsFilter
          :selectedContractors="selectedContractors"
          :contractors="contractors"
          @selectedContractorsInput="handleSelectedContractorsInput"
        />
        <v-divider />
        <ProjectsFilter
          :selectedProjects="selectedProjects"
          :projects="projects"
          @selectedProjectsInput="handleSelectedProjectsInput"
        />
        <v-divider />
        <StatusFilter
          :selectedStatuses="selectedStatuses"
          @selectedStatusInput="handleSelectedStatusInput"
        />
        <v-divider />
        <CurrenciesFilter
          :currencies="currencies"
          :selectedCurrencies="selectedCurrencies"
          @selectedCurrenciesInput="handleSelectedCurrenciesInput"
        />
      </v-list>
      <v-divider />
    </v-navigation-drawer>
</template>

<script>
import dayjs from 'dayjs'
import { ISO_DATE_FORMAT } from '@/helpers'

export default {
  name: 'InsightsFilters',
  props: {
    areas: {
      type: Array,
      required: true,
      default: () => []
    },
    contracts: {
      type: Array,
      required: true,
      default: () => []
    },
    packages: {
      type: Array,
      required: true,
      default: () => []
    },
    contractors: {
      type: Array,
      required: true,
      default: () => []
    },
    projects: {
      type: Array,
      required: true,
      default: () => []
    },
    currencies: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  emits: ['filtersChanged', 'dateRangeChanged'],
  data () {
    return {
      dateFrom: null,
      dateTo: null,
      filtersWidth: 225,
      selectedAreas: [],
      selectedContracts: [],
      selectedPackages: [],
      selectedContractors: [],
      selectedProjects: [],
      selectedStatuses: [],
      selectedCurrencies: [],
      filters: []
    }
  },
  computed: {
    contractFilter () {
      if (!this.selectedContracts.length && !this.selectedPackages.length) {
        return null
      }

      const packageNames = new Set(this.packages
        .filter(item => this.selectedPackages.includes(item.id))
        .map(item => item.name))
      const values = this.contracts
        .filter(contract => (!this.selectedPackages.length || packageNames.has(contract.package)) &&
          (!this.selectedContracts.length || this.selectedContracts.includes(contract.id)))
        .map(contract => contract.id)

      return values.length
        ? this.buildEqualsFilter('contracts.id', values)
        : { member: 'contracts.id', operator: 'notSet' }
    }
  },
  methods: {
    buildEqualsFilter (dimension, values) {
      return {
        member: dimension,
        operator: 'equals',
        values
      }
    },
    setNewFilter (dimension, filter) {
      this.deleteFilter(dimension)
      this.filters.push(filter)
    },
    deleteFilter (dimension) {
      this.filters = this.filters.filter((f) => f.member !== dimension)
    },
    handleSelectedAreaInput (value) {
      this.selectedAreas = value
    },
    handleSelectedStatusInput (value) {
      this.selectedStatuses = value
    },
    handleSelectedCurrenciesInput (value) {
      this.selectedCurrencies = value
    },
    handleSelectedContractsInput (value) {
      this.selectedContracts = value
    },
    handleSelectedPackagesInput (value) {
      this.selectedPackages = value
    },
    handleSelectedContractorsInput (value) {
      this.selectedContractors = value
    },
    handleSelectedProjectsInput (value) {
      this.selectedProjects = value
    },
    handleDateFromInput (value) {
      this.dateFrom = value
      this.dateFromMenu = false
    },
    handleDateToInput (value) {
      this.dateTo = value
      this.dateToMenu = false
    },
    handleDateRangeChange () {
      if (this.dateFrom && this.dateTo) {
        return this.$emit('dateRangeChanged', [this.dateFrom, this.dateTo])
      } else if (this.dateFrom && !this.dateTo) {
        return this.$emit('dateRangeChanged', [this.dateFrom, dayjs().format(ISO_DATE_FORMAT)])
      } else if (!this.dateFrom && this.dateTo) {
        return this.$emit('dateRangeChanged', [dayjs().subtract(100, 'year').format(ISO_DATE_FORMAT), this.dateTo])
      }
      return this.$emit('dateRangeChanged', [])
    },
    updateFilters (filterDimension, filterValues) {
      if (filterValues.length === 0) {
        this.deleteFilter(filterDimension)
        this.$emit('filtersChanged', this.filters)
        return
      }

      const filters = this.buildEqualsFilter(filterDimension, filterValues)
      this.setNewFilter(filterDimension, filters)
      this.$emit('filtersChanged', this.filters)
    }
  },
  watch: {
    selectedAreas (val) {
      const filterDimension = 'areas.code'
      const filterValues = val.map(a => a.code)

      this.updateFilters(filterDimension, filterValues)
    },
    selectedStatuses (values) {
      const filterDimension = 'contracts.active'
      const filterValues = values.map(s => s.value)

      this.updateFilters(filterDimension, filterValues)
    },
    selectedCurrencies (values) {
      const filterDimension = 'currencies.id'
      const filterValues = values.map(c => c.id)

      this.updateFilters(filterDimension, filterValues)
    },
    contractFilter (filter) {
      this.deleteFilter('contracts.id')
      if (filter) {
        this.filters.push(filter)
      }
      this.$emit('filtersChanged', this.filters)
    },
    selectedContractors (values) {
      const filterDimension = 'companies.id'

      this.updateFilters(filterDimension, values)
    },
    selectedProjects (values) {
      const filterDimension = 'projects.id'

      this.updateFilters(filterDimension, values)
    },
    dateFrom (val) {
      this.handleDateRangeChange()
    },
    dateTo (val) {
      this.handleDateRangeChange()
    }
  }
}
</script>

<style scoped>

  .v-list-item {
    min-height: auto;
  }

  :deep(.insights-filter-title) {
    font-size: 0.90rem;
    font-weight: 500;
  }

  :deep(.insights-filter-text) {
    font-size: 0.78rem
  }

  :deep(.insights-filter-input-text) {
    font-size: 0.85rem
  }

  :deep(.insights-filter-input-text .v-label) {
    font-size: 0.85rem
  }

  :deep(.full-width) {
    width: 100%;
  }

</style>
